import React, { useState } from 'react';
import { Crown } from 'lucide-react';
import type { LeaderboardItem } from '../authAdapter';
import top100 from '../top100.json';

const RankIcon = ({ rank }: { rank: number }) => {
  if (rank === 1)
    return <span className="text-[20px] font-['Poppins']">🥇</span>;
  if (rank === 2)
    return <span className="text-[20px] font-['Poppins']">🥈</span>;
  if (rank === 3)
    return <span className="text-[20px] font-['Poppins']">🥉</span>;
  return (
    <span className="text-[20px] text-white font-['Poppins']">{rank}</span>
  );
};

export const Leaderboard: React.FC = () => {
  // 直接使用前端写死的 top100 数据，不再从后端获取
  const [data] = useState<LeaderboardItem[]>(top100 as LeaderboardItem[]);
  const loading = false;
  const error: string | null = null;

  const formatAddress = (address: string): string => {
    if (!address) return '--';
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatValue = (value: string | number): string => {
    if (typeof value === 'number') {
      if (Number.isInteger(value)) {
        return value.toLocaleString();
      }
      return parseFloat(value.toFixed(4)).toString();
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (Number.isInteger(numValue)) {
        return numValue.toLocaleString();
      }
      return parseFloat(numValue.toFixed(4)).toString();
    }
    return value;
  };

  return (
    <div className="relative w-full rounded-3xl p-4 md:p-8 flex flex-col h-[600px] overflow-hidden">
      <div className="absolute inset-0 bg-[url('/apr-points/bg_leaderboard.png')] bg-cover bg-center opacity-[0.08]" />
      <div className="absolute inset-0 bg-primarybrand opacity-10 pointer-events-none" />
      <div
        className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{
          border: '1px solid transparent',
          background:
            'linear-gradient(94.02deg, rgba(58, 236, 167, 0.48) 0%, rgba(154, 238, 230, 0.24) 100%) border-box',
          WebkitMask:
            'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      <div className="relative z-10 flex-shrink-0">
        <div className="flex items-center gap-2 mb-4 md:mb-8">
          <Crown className="w-[20px] h-[20px]" />
          <h2 className="text-[20px] font-medium text-white font-['Poppins']">
            Leaderboard
          </h2>
        </div>

        <div className="grid grid-cols-[1fr_2fr_1.5fr] md:grid-cols-3 text-[12px] font-medium text-white/60 mb-4 px-2 md:px-4 font-['Poppins']">
          <div>Rank</div>
          <div>Address</div>
          <div className="text-right">Points</div>
        </div>
      </div>

      <div className="relative z-10 flex-1 overflow-y-auto min-h-0">
        {!loading && !error && (
          <>
            {data.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">No data available</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.map((item, index) => (
                  <div
                    key={`${item.rank}-${item.address}-${index}`}
                    className="grid grid-cols-[1fr_2fr_1.5fr] md:grid-cols-3 items-center px-2 md:px-4 py-3 md:py-4 rounded-xl hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 font-['Poppins']"
                  >
                    <div>
                      <RankIcon rank={item.rank} />
                    </div>
                    <div className="text-sm md:text-[20px] text-gray-300 font-['Poppins']">
                      {formatAddress(item.address)}
                    </div>
                    <div className="text-right text-sm md:text-[20px] font-medium text-white font-['Poppins']">
                      {formatValue(item.value)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
